import {processTimingEvent} from 'performance/BenchlingProfiler';
import {toDatadogTagFormat} from 'performance/PerformanceUtils';
import {ResultViewType} from 'results/assays/AssayResultsConstants';

import {EventMetadata, NOT_SPECIFIED, ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

export const LOAD_RESULTS_TAB_DISTRIBUTION_NAME = 'perf.load_results_tab';

const DOMAIN = ProfilerEventDomain.RESULTS;
/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
export const PROFILER_RESULTS_EVENTS_VERSION = 1;

enum ProfilerResultsFeatures {
  LOAD_RESULTS_TAB = 'loadResultsTab',
}

enum ProfilerResultsActions {
  LOAD_RESULTS_TAB_STARTED = 'loadResultsTabStarted',
  LOAD_RESULTS_TAB_FINISHED = 'loadResultsTabFinished',
}

/**
 * Only `FINISHED` events need to be processed.
 */
const FINISHED_ACTION_TO_PROCESSING_FUNCTION: {[key in ProfilerResultsActions]?: Function} = {
  [ProfilerResultsActions.LOAD_RESULTS_TAB_FINISHED]: processLoadResultsTab,
};

export const LOAD_RESULTS_TAB_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerResultsFeatures.LOAD_RESULTS_TAB,
  action: ProfilerResultsActions.LOAD_RESULTS_TAB_STARTED,
};

export const LOAD_RESULTS_TAB_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerResultsFeatures.LOAD_RESULTS_TAB,
  action: ProfilerResultsActions.LOAD_RESULTS_TAB_FINISHED,
};

function getBucket(value: number, bucketCutoffs: Array<number>): string {
  const max = bucketCutoffs.find((cutoff) => cutoff > value) ?? 'max';
  const min = bucketCutoffs.findLast((cutoff) => cutoff <= value) ?? 'min';
  return `${min}-to-${max}`;
}

export function getLoadResultsTabExtraMetadata(
  numResults: number,
  numSchemas: number,
  resultType?: ResultViewType
): EventMetadata {
  // Bucket `distributionMetadata` to reduce DataDog cost
  const numResultsBucket = getBucket(numResults, [0, 100, 1_000, 10_000]);
  const numSchemasBucket = getBucket(numSchemas, [0, 10, 100]);
  const resultTypeTag = resultType ? toDatadogTagFormat(resultType) : NOT_SPECIFIED;
  return {
    distributionMetadata: {
      num_results: numResultsBucket,
      num_schemas: numSchemasBucket,
      result_type: resultTypeTag,
    },
    actionMetadata: {
      num_results: numResultsBucket,
      num_schemas: numSchemasBucket,
      result_type: resultTypeTag,
    },
  };
}

export function processResultsEvents(profilerEvent: ProfilerEvent): void {
  FINISHED_ACTION_TO_PROCESSING_FUNCTION[profilerEvent.action as ProfilerResultsActions]?.(profilerEvent);
}

function processLoadResultsTab(profilerFinishedEvent: ProfilerEvent): void {
  const {id} = profilerFinishedEvent;
  processTimingEvent(
    {...LOAD_RESULTS_TAB_STARTED_EVENT, id},
    {...LOAD_RESULTS_TAB_FINISHED_EVENT, id},
    'Load Results Tab',
    LOAD_RESULTS_TAB_DISTRIBUTION_NAME,
    PROFILER_RESULTS_EVENTS_VERSION
  );
}
